import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone, Loader2, CheckCircle2, Calendar } from "lucide-react";
import PageHero from "@/components/common/PageHero";
import CalendlyEmbed from "@/components/common/CalendlyEmbed";
import { submitLead } from "@/lib/api";

export default function ContactPage() {
  const [params] = useSearchParams();
  const initialType = params.get("type") === "audit" ? "audit" : "strategy_call";
  const [form, setForm] = useState({
    name: "", email: "", company: "", website: "", monthly_revenue: "", message: "",
    lead_type: initialType,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead(form);
      setDone(true);
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", website: "", monthly_revenue: "", message: "", lead_type: form.lead_type });
    } catch (err) {
      toast.error("Something went wrong. Please email info@kavionglobal.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Get in Touch"
        title={<>Let's <span className="text-gradient">scale your brand.</span></>}
        subtitle="Tell us a little about your store. We'll send back a free audit and a 30-min strategy call slot."
      />

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-3xl glass-heavy p-7 md:p-10"
            >
              <div className="flex gap-2 mb-6 p-1 rounded-full bg-white/5 w-fit">
                {[
                  ["strategy_call", "Strategy Call"],
                  ["audit", "Free Store Audit"],
                  ["contact", "General"],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`lead-type-${v}`}
                    onClick={() => setForm({ ...form, lead_type: v })}
                    className={`px-4 py-2 rounded-full text-xs font-semibold transition ${
                      form.lead_type === v ? "bg-white text-black" : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field name="name" label="Full Name *" value={form.name} onChange={onChange} testid="contact-name" />
                <Field name="email" label="Email *" type="email" value={form.email} onChange={onChange} testid="contact-email" />
                <Field name="company" label="Company / Brand" value={form.company} onChange={onChange} testid="contact-company" />
                <Field name="website" label="Shopify URL" value={form.website} onChange={onChange} testid="contact-website" />
              </div>
              <div className="mt-4">
                <Field name="monthly_revenue" label="Monthly revenue (approx)" placeholder="e.g. $50K/month" value={form.monthly_revenue} onChange={onChange} testid="contact-revenue" />
              </div>
              <div className="mt-4">
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Tell us about your goals</label>
                <textarea
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message"
                  placeholder="Anything we should know..."
                  className="w-full bg-black/40 border border-white/10 focus:border-blue-500/40 rounded-2xl px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition"
                />
              </div>
              <button
                type="submit"
                disabled={submitting || done}
                data-testid="contact-submit"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 transition-all hover:scale-[1.01] text-white rounded-full px-7 py-4 font-semibold glow-blue w-full sm:w-auto"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : done ? <CheckCircle2 className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                {submitting ? "Sending..." : done ? "Sent — we'll be in touch" : "Submit Request"}
              </button>
              <p className="mt-4 text-xs text-zinc-500">No spam. We respond within 24 hours.</p>
            </form>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 space-y-4">
            <div className="rounded-3xl glass p-7">
              <h3 className="font-display text-2xl font-bold text-white">Hong Kong HQ</h3>
              <div className="mt-5 space-y-4 text-sm text-zinc-400">
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 text-blue-400 shrink-0" />
                  <span>UNIT 2904-05, 29/F Universal Trade Ctr,<br /> 3 Arbuthnot Rd, Central, Hong Kong</span>
                </div>
                <a href="mailto:info@kavionglobal.com" className="flex items-center gap-3 hover:text-white transition">
                  <Mail className="h-4 w-4 text-blue-400" /> info@kavionglobal.com
                </a>
                <a href="https://wa.me/15074325890" className="flex items-center gap-3 hover:text-white transition">
                  <Phone className="h-4 w-4 text-blue-400" /> +1 (507) 432-5890 · WhatsApp
                </a>
              </div>
            </div>

            <div className="rounded-3xl glass p-7">
              <h3 className="font-display text-2xl font-bold text-white">What happens next</h3>
              <ol className="mt-5 space-y-4 text-sm text-zinc-400">
                {[
                  "We review your store + ad accounts",
                  "We send a personalised audit (PDF, 24h)",
                  "We hop on a 30-min strategy call",
                  "You walk away with a roadmap — free",
                ].map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="h-6 w-6 grid place-items-center rounded-full bg-blue-500/15 border border-blue-400/20 text-blue-300 text-xs font-bold shrink-0">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          </div>

          {/* Calendly Embed */}
          <div id="book" className="mt-20 scroll-mt-32">
            <div className="max-w-3xl mb-10">
              <span className="text-xs uppercase tracking-[0.25em] text-blue-400 font-bold inline-flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" /> Schedule Directly
              </span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
                Pick a 30-min slot that <span className="text-gradient">works for you.</span>
              </h2>
              <p className="mt-4 text-zinc-400">
                Prefer to skip the form? Grab a slot directly on our calendar — we'll show up with insights for your brand.
              </p>
            </div>
            <CalendlyEmbed />
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ name, label, type = "text", value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-black/40 border border-white/10 focus:border-blue-500/40 rounded-2xl px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition"
      />
    </label>
  );
}
